"use client";

import { NumericInput } from "@kyan/ui";
import React, { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { getOptionOrPerpConstraints } from "#/config/orders";
import { validatePerpSize } from "#/hooks/useValidatePerpSize";
import { useUserScopes } from "#/services/dynamic/hooks/useUserScopes";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { type UnderlyingSymbol } from "#/types/markets";
import { RiskPosition } from "#/types/positions";
import { type InstrumentType } from "#/types/trade";
import { isOptionInstrument } from "#/utils/instrument.utils";
import { validateOptionSize } from "#/utils/validateOptionSize";

export interface SizeInputProps {
  value: string;
  type: InstrumentType;
  isBuy: boolean;
  underlying: UnderlyingSymbol;
  instrument: string;
  nettedOptionPortfolio: RiskPosition[];
  isRfqOptionsTab: boolean;
  glowEffect: boolean;
  onValueChange?: (value: string) => void;
}

export const SizeInput: React.FC<SizeInputProps> = ({
  value,
  isBuy,
  instrument,
  nettedOptionPortfolio,
  isRfqOptionsTab,
  type,
  underlying,
  onValueChange,
  glowEffect,
}) => {
  const { isDevMode } = useIsDevMode();
  const { hasEarlyAccessVip } = useUserScopes();
  const [sizeError, setSizeError] = useState("");
  const [debouncedInputValue] = useDebounceValue(value, 600);

  const isOption = isOptionInstrument(type);
  const { sizeDisplayPrecision } = getOptionOrPerpConstraints(instrument);

  const hasNoGuardRails = isDevMode || hasEarlyAccessVip;
  const nettedPositions = nettedOptionPortfolio.find(
    ({ instrument: positionInstrument }) => positionInstrument === instrument,
  );

  useEffect(() => {
    if (!isOption && isRfqOptionsTab) {
      setSizeError("");
      return;
    }

    if (!isOption) {
      const { sizeError: perpSizeError, sizeWarning } = validatePerpSize({
        underlying,
        size: debouncedInputValue,
        isShort: true,
        hasNoGuardRails,
        isUnderlyingSelected: true,
      });

      setSizeError(perpSizeError || sizeWarning);
      return;
    }

    setSizeError(
      debouncedInputValue.length
        ? validateOptionSize({
            size: debouncedInputValue,
            underlying,
            isRfq: isRfqOptionsTab,
            isBuy,
            isShortError: true,
            nettedPositions: nettedPositions?.size ?? 0,
            hasNoGuardRails,
          })
        : "",
    );
  }, [
    debouncedInputValue,
    isRfqOptionsTab,
    underlying,
    isOption,
    hasNoGuardRails,
    isBuy,
    nettedPositions,
  ]);

  return (
    <NumericInput
      value={value}
      error={sizeError}
      className="w-full"
      variant="secondary"
      glowEffect={glowEffect}
      disabled={isRfqOptionsTab && !isOption}
      rightIcon={isOption ? "Qty" : underlying}
      autoNumericOptions={{
        decimalPlaces: sizeDisplayPrecision,
      }}
      onChange={onValueChange}
    />
  );
};
